import { HubConnectionState } from '@microsoft/signalr';
import { action, computed, reaction, makeObservable } from 'mobx';
import { examJoinedState } from '../ExamJoinedState';
import { Disposers, logger, toastrHelper } from '../../../lib/helpers';
import { HomeworkHubMessage, HomeworkRole } from '../../homework/homework-models';
import { ExamTasks } from './exam-tasks';
import { ExamJoinStore } from './ExamJoinStore';
import { ExamKeyValuesStore } from './ExamKeyValuesStore';
import { ExamStateStore } from './ExamStateStore';
import { ExamTaskStore } from './ExamTaskStore';
import { HubConnectionStore } from './HubConnectionStore';
import swal from 'sweetalert2';

export class ExamHubStore {
    readonly connection: HubConnectionStore;
    readonly join: ExamJoinStore;
    readonly state: ExamStateStore;
    readonly examKeyValues: ExamKeyValuesStore;
    readonly tasks: ExamTaskStore;

    private readonly disposers = new Disposers();

    constructor(readonly examId: number,
        readonly role: HomeworkRole) {
        makeObservable(this);
        this.connection = new HubConnectionStore();
        this.disposers.add(this.connection);
        this.join = new ExamJoinStore(examId, role);
        this.state = new ExamStateStore();      
        this.examKeyValues = new ExamKeyValuesStore();
        this.tasks = new ExamTaskStore();
        this.connection.connection.on('message', this.onReceivedMessage);
        this.setup();
    }

    @action
    start() {
        this.connection.start();
    }

    @computed
    get version(): number | null {
        const examState = this.state.examState;
        return examState === null
            ? null
            : examState.Version;
    }

    @computed
    get lessonSnapshotId(): number | null {
        const examState = this.state.examState;
        return examState === null
            ? null
            : examState.LessonSnapshotId;
    }

    @computed
    get currentPageId(): number | null {
        const examState = this.state.examState;
        return examState === null
            ? null
            : examState.CurrentPageId;
    }

    @computed
    get currentPushableBlockId(): number | null {
        const examState = this.state.examState;
        return examState === null
            ? null
            : examState.CurrentPushableBlockId;
    }

    @computed
    get revealedBlockIds(): ReadonlySet<number> {
        return this.examKeyValues.revealedBlockIds;
    }

    @computed
    get examBlockIds(): ReadonlySet<number> {
        return this.examKeyValues.examBlockIds;
    }

    private setup() {
        examJoinedState.setProvider(this.join);
        this.disposers.add(() => examJoinedState.setProvider(null));

        this.disposers.add(
            reaction(
                () => this.connection.connectionState,
                this.onConnectionStateChange,
                { fireImmediately: true }),
            reaction(
                () => this.join.state?.value,
                this.onJoinStateChange
            ),
            reaction(
                () => this.state.examState,
                this.onExamStateChange
            ),
            reaction(
                () => [this.tasks.any, this.tasks.active, this.tasks.state?.value],
                this.onTasksStateChange
            ),
            reaction(
                () => this.examKeyValues.allChanges,
                this.onKeyValueUpdates
            ),
            reaction(
                () => this.examKeyValues.missingChangeStamps.size,
                this.onMissingChangeStamps
            ));
        // TODO: reactions
    }

    @action
    private onConnectionStateChange = () => {
        logger.log('ExamHubStore.onConnectionStateChange');
        const state = this.connection.connectionState;
        if (state === HubConnectionState.Connected) {
            this.join.join(this.connection.connection);
        }
        else {
            this.join.reset();
            this.state.clearExamState();
            this.examKeyValues.clear();
            this.tasks.clearAll();
        }
    };

    @action
    private onJoinStateChange = () => {
        const state = this.join.state;
        if (state !== null && state.isResolved)
            this.onReceivedMessage(state.value);
        if (this.join.joined)
            this.tasks.activate();
        else
            this.tasks.deactivate();
    };

    @action
    private onExamStateChange = () => {
        let state = this.state.examState;
        if (state === null) {
            logger.log('onExamStateChange clearing state');
            this.examKeyValues.clear();
        }
    };

    @action
    private onTasksStateChange = () => {
        if (!this.tasks.active)
            return;
        const state = this.tasks.state;

        if (state !== null) {
            if (state.isResolved) {
                this.onReceivedMessage(state.value);
            }
            if (state.isRejected) {
                this.onError(state.error);
            }
        }

        if (!this.tasks.active)
            return;
        this.tasks.runNext(this.connection.connection);
    };

    @action
    private onKeyValueUpdates = () => {
        if (!this.tasks.active || this.examKeyValues.changes.size === 0)
            return;
        this.tasks.add(ExamTasks.updateKeyValues(this.examId, this.examKeyValues.allChanges));
    };

    @action
    private onMissingChangeStamps = () => {
        if (!this.tasks.active || this.examKeyValues.missingChangeStamps.size === 0)
            return;
        const missingChangeStamps = Array.from(this.examKeyValues.missingChangeStamps);
        this.tasks.add(ExamTasks.getKeyValuesPart(this.examId, missingChangeStamps));
    };

    @action
    private onReceivedMessage = async (message: HomeworkHubMessage) => {
        logger.log('ExamHubStore.onReceivedMessage', message);

        switch (message.MessageType) {
            case 'bad-request':
                toastrHelper.error(message.Error);
                break;
            case 'disconnect':
                this.connection.dispose();
                this.connection.dontReconnectMessage = message.Error;
                break;
            case 'reset-connection':
                this.connection.reconnect();
                break;
            case 'homework-joined':
                this.state.updateExamState(message.HomeworkState);
                this.examKeyValues.mergeComplete(message.KeyValues);
                break;
            case 'homework-state':
                this.state.updateExamState(message.HomeworkState);
                break;
            case 'homework-key-values-updated':
                this.state.updateExamState(message.HomeworkState);
                this.examKeyValues.mergePartial(message.ChangeStamps, message.Changes);
                break;
            case 'homework-key-values-part':
                this.examKeyValues.mergePartial(message.ChangeStamps, message.KeyValues);
                break;
            case 'debug':
                logger.log('payload', message.Payload);
                break;
            case 'homework-content-updated':
                await swal.fire({
                    html: 'Exam content was updated.<br> The page will reload in 5 seconds',
                    timer: 5000,
                    didOpen: () => swal.showLoading(),
                    showCancelButton: false,
                    showCloseButton: false,
                    showConfirmButton: false,
                });
                window.location.reload();
                break;
            default:
                break;
        }
    };

    @action
    private onError(error?: any | undefined) {
        logger.log('ExamHubStore.onError', error);
    }

    dispose = () => {
        this.disposers.dispose();
    };
}
